import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Button, Space, Image, Tag, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { getNewcomerChallengeDetail } from './api';
import type { NewcomerChallenge } from './types';
import dayjs from 'dayjs';

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '未开始', color: 'default' },
  1: { text: '投稿中', color: 'processing' },
  2: { text: '评比中', color: 'warning' },
  3: { text: '已结束', color: 'success' },
};

const NewcomerChallengeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<NewcomerChallenge | null>(null);

  useEffect(() => {
    if (id) {
      setLoading(true);
      getNewcomerChallengeDetail(Number(id))
        .then((res) => {
          if (res.status === 200) {
            setDetail(res.data);
          } else {
            message.error(res.message || '获取详情失败');
          }
        })
        .catch(() => {
          message.error('获取详情失败');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  if (!detail) {
    return <Card loading={loading}>加载中...</Card>;
  }

  return (
    <Card
      title="新人王挑战赛详情"
      extra={
        <Space>
          <Button onClick={() => navigate(-1)}>返回</Button>
          <Button
            type="primary"
            onClick={() => navigate(`/newcomer-challenge/submissions/${id}`)}
          >
            查看投稿
          </Button>
        </Space>
      }
    >
      <Descriptions column={2} bordered>
        <Descriptions.Item label="活动ID">{detail.id}</Descriptions.Item>
        <Descriptions.Item label="活动标题">{detail.title}</Descriptions.Item>
        <Descriptions.Item label="副标题">{detail.subtitle}</Descriptions.Item>
        <Descriptions.Item label="状态">
          <Tag color={STATUS_MAP[detail.status || 0]?.color}>
            {STATUS_MAP[detail.status || 0]?.text}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="投稿开始时间">
          {detail.submitBeginTime
            ? dayjs(Number(detail.submitBeginTime)).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="投稿结束时间">
          {detail.submitEndTime
            ? dayjs(Number(detail.submitEndTime)).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="投票开始时间">
          {detail.voteBeginTime
            ? dayjs(Number(detail.voteBeginTime)).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="投票结束时间">
          {detail.voteEndTime
            ? dayjs(Number(detail.voteEndTime)).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="活动简介" span={2}>
          {detail.eventDescription || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="其他奖励描述" span={2}>
          {detail.otherRewardDesc || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="规则说明ID">
          {detail.ruleId || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="点赞权重">
          {detail.likeWeight || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="创建人">
          {detail.updateUserName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="首页推荐(日语)">
          {detail.homeRecommendJa ? (
            <Image
              src={detail.homeRecommendJa}
              alt="首页推荐(日语)"
              style={{ width: 100, height: 100, objectFit: 'cover' }}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="首页推荐(英语)">
          {detail.homeRecommend ? (
            <Image
              src={detail.homeRecommend}
              alt="首页推荐(英语)"
              style={{ width: 100, height: 100, objectFit: 'cover' }}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="奖励设置" span={2}>
          {detail.rewards && detail.rewards.length > 0 ? (
            <div>
              {detail.rewards.map((reward, index) => (
                <div key={index} style={{ marginBottom: 8 }}>
                  {reward.rank}: 人数 {reward.num}, 活动金币 {reward.purpleCoins}枚,
                  现金 {reward.cash}
                  {reward.vipBenefits && `, VIP权益: ${reward.vipBenefits}`}
                </div>
              ))}
            </div>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default NewcomerChallengeDetailPage;

